import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { TopicDetailDto } from "../types";
import AppSearchSelect from "../../../../components/AppSearchSelect/AppSearchSelect";
import { getTranslatedDescription } from "../../../../utils/formatters/UserLanguageToDescriptionField";
import i18n from "../../../../i18n";
import { useUserPermissions } from "../../../../utils/auth";
import { PERMISSIONS } from "../../../../constants/permissions";

interface TopicDetailFormProps {
    isLoading: boolean;
    editableDetail: TopicDetailDto | null;
    isEditing: boolean;
    isCreating: boolean;
    isSaving: boolean;
    errors: { [key: string]: string | undefined };
    setIsEditing: (value: boolean) => void;
    setEditableDetail: React.Dispatch<React.SetStateAction<TopicDetailDto | null>>;
    setValidationErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string | undefined }>>;
    topicOptions: SelectItemDto[];
    onDelete: () => void;
    onCancel: () => void;
    onSave: () => void;
}

export default function TopicDetailForm({
    isLoading,
    editableDetail,
    isEditing,
    isCreating,
    isSaving,
    errors,
    setIsEditing,
    setEditableDetail,
    setValidationErrors,
    topicOptions,
    onDelete,
    onCancel,
    onSave,
}: TopicDetailFormProps) {
    const { t } = useTranslation(["topic", "common"]);
    const detailName = editableDetail?.description;
    const permissions = useUserPermissions();

    return (
        <div className="border p-3 topics-management-panel">
            {isLoading ? (
                <div className="topics-management-loading">
                    <Spinner animation="border" />
                </div>
            ) : (
                <>
                    <Row>
                        <Col xs={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t("common:code")}</Form.Label>
                                <Form.Control
                                    value={editableDetail?.code || ""}
                                    readOnly={!isEditing}
                                    isInvalid={!!errors.code}
                                    onChange={(e) => {
                                        setEditableDetail((prev) =>
                                            prev ? { ...prev, code: e.target.value } : prev
                                        );
                                        setValidationErrors((prev) => ({ ...prev, code: undefined }));
                                    }}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.code}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col xs={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t("topic:mainTopicCode")}</Form.Label>
                                {isEditing ? (
                                    <AppSearchSelect
                                        isMulti={false}
                                        value={topicOptions.find(topic => topic.id == editableDetail?.parentId) ?? null}
                                        options={topicOptions}
                                        placeholder={t('common:search')}
                                        onChange={(selectedOption: SelectItemDto | SelectItemDto[] | null) => {
                                            setEditableDetail((prev) =>
                                                prev
                                                    ? {
                                                          ...prev,
                                                          parentId: selectedOption === null ? undefined : Number((selectedOption as SelectItemDto).id),
                                                      }
                                                    : prev
                                            );
                                            setValidationErrors((prev) => ({ ...prev, parentId: undefined }));
                                        }}
                                    />
                                ) : (
                                    <Form.Control value={getTranslatedDescription(editableDetail?.parentDescription, i18n.language) || ""} readOnly />
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>{t("topic:nameCat")}</Form.Label>
                        <Form.Control
                            value={detailName?.catalan || ""}
                            readOnly={!isEditing}
                            isInvalid={!!errors["description.catalan"]}
                            onChange={(e) => {
                                setEditableDetail((prev) =>
                                    prev ? 
                                        {
                                            ...prev,
                                            description: {
                                                ...prev.description,
                                                catalan: e.target.value,
                                            },
                                        } : prev
                                );
                                setValidationErrors((prev) => ({ ...prev, "description.catalan": undefined }));
                            }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors["description.catalan"]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t("topic:nameEs")}</Form.Label>
                        <Form.Control
                            value={detailName?.spanish || ""}
                            readOnly={!isEditing}
                            isInvalid={!!errors["description.spanish"]}
                            onChange={(e) => {
                                setEditableDetail((prev) =>
                                    prev ? {
                                            ...prev,
                                            description: {
                                                ...prev.description,
                                                spanish: e.target.value,
                                            },
                                        } : prev
                                );
                                setValidationErrors((prev) => ({ ...prev, "description.spanish": undefined }));
                            }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors["description.spanish"]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t("topic:nameEn")}</Form.Label>
                        <Form.Control
                            value={detailName?.english || ""}
                            readOnly={!isEditing}
                            isInvalid={!!errors["description.english"]}
                            onChange={(e) => {
                                setEditableDetail((prev) =>
                                    prev ? {
                                            ...prev,
                                            description: {
                                                ...prev.description,
                                                english: e.target.value,
                                            },
                                        } : prev
                                );
                                setValidationErrors((prev) => ({ ...prev, "description.english": undefined }));
                            }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors["description.english"]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t("topic:promptExamples")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={editableDetail?.promptExample || ""}
                            readOnly={!isEditing}
                            onChange={(e) =>
                                setEditableDetail((prev) =>
                                    prev ? {
                                        ...prev,
                                        promptExample: e.target.value,
                                    } : prev
                                )
                            }
                        />
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label>{t("topic:responseModel")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={editableDetail?.responseModel || ""}
                            readOnly={!isEditing}
                            onChange={(e) =>
                                setEditableDetail((prev) =>
                                    prev ? {
                                            ...prev,
                                            responseModel: e.target.value,
                                        } : prev
                                )
                            }
                        />
                    </Form.Group>

                    <div className="topics-management-actions">
                        {permissions.length > 0 && permissions.includes(PERMISSIONS.TOPIC_MANAGEMENT.DELETE) && (
                            <Button
                                variant="outline-danger"
                                className="rounded-pill px-4"
                                onClick={onDelete}
                                disabled={!editableDetail || isSaving}
                            >
                                {t("topic:cancelTopic")}
                            </Button>
                        )}

                        {isEditing ? (
                            <div className="topics-management-actions-right">
                                <Button
                                    variant="secondary"
                                    className="rounded-pill px-4"
                                    onClick={onCancel}
                                    disabled={isSaving}
                                >
                                    {t("common:buttons.buttonCancel")}
                                </Button>
                                <Button
                                    variant="primary"
                                    className="rounded-pill px-4"
                                    onClick={onSave}
                                    disabled={!editableDetail || isSaving}
                                >
                                    {isSaving ? t("common:buttons.buttonSaveingProgress") : t("common:buttons.buttonSave")}
                                </Button>
                            </div>
                        ) : (
                            permissions.length > 0 && permissions.includes(PERMISSIONS.TOPIC_MANAGEMENT.EDIT) && (
                                <div className="topics-management-actions-right">
                                    <Button
                                        variant="primary"
                                        className="rounded-pill px-4"
                                        onClick={() => setIsEditing(true)}
                                        disabled={!editableDetail || isEditing || isCreating}
                                    >
                                        {t("common:buttons.buttonEdit")}
                                    </Button>
                                </div>
                            )
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
